import { Sparkle } from '@phosphor-icons/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createRoute, useNavigate } from '@tanstack/react-router';
import { useRef, useState } from 'react';
import { cleanId } from '../../shared/clean-id.ts';
import { accountsQuery, api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice } from '../common.tsx';
import { ChatComposer } from '../components/chat-composer.tsx';
import { ChatSidebar } from '../components/chat.tsx';
import { PendingRequests } from '../components/pending-requests.tsx';
import { useChatAttachments } from '../use-chat-attachments.ts';
import { Route as parent } from './signed-in.tsx';
export const Route = createRoute({ getParentRoute: () => parent, path: '/new', component: NewSite });

function NewSite() {
  const [message, setMessage] = useState('');
  const chatId = useRef<string | null>(null);
  const creatingSite = useRef<Promise<string> | null>(null);
  const request = useRef<{ signature: string; runId: string } | null>(null);
  const accounts = useQuery(accountsQuery);
  const navigate = useNavigate();
  const ensureChat = async () => {
    if (chatId.current) return chatId.current;
    if (!creatingSite.current)
      creatingSite.current = api
        .POST('/api/chats', { body: {} })
        .then((result) => {
          chatId.current = unwrap(result).id;
          return chatId.current;
        })
        .catch((error) => {
          creatingSite.current = null;
          throw error;
        });
    return creatingSite.current;
  };
  const attachments = useChatAttachments(ensureChat);
  const create = useMutation({
    mutationFn: async () => {
      if (attachments.blocked)
        throw new Error('Finish uploading or remove failed attachments before continuing.');
      const id = await ensureChat();
      const text = message.trim() || 'Help me create a website using the attached materials.';
      const signature = JSON.stringify({ text, attachmentIds: attachments.ids });
      if (request.current?.signature !== signature) request.current = { signature, runId: cleanId() };
      return unwrap(
        await api.POST('/api/chats/{chatId}/messages', {
          params: { path: { chatId: id } },
          body: {
            message: text,
            runId: request.current.runId,
            attachmentIds: attachments.ids,
            source: 'new-site',
          },
        }),
      );
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['sites'] }),
        queryClient.invalidateQueries({ queryKey: ['chats'] }),
      ]);
      await navigate({
        to: '/chats/$chatId',
        params: { chatId: chatId.current! },
      });
    },
  });
  const canStart =
    !create.isPending &&
    Boolean(message.trim() || attachments.ids.length) &&
    !attachments.blocked &&
    !accounts.isPending;
  return (
    <main className="flex flex-col h-[calc(100dvh_-_var(--app-header-height))] overflow-hidden min-[761px]:flex-row">
      <ChatSidebar hideWhenEmpty />
      <div className="flex-1 min-w-0 min-h-0 flex flex-col overflow-hidden h-full [&_>_.new-site]:flex-1 [&_>_.new-site]:overflow-y-auto [&_>_.new-site]:w-full">
        <div className="new-site mx-auto my-0 px-7 max-w-197.5 pt-8.5 pb-22.5">
          <PendingRequests className="mx-auto mb-6 max-w-lg" />
          <div className="px-0 text-center pt-11.5 pb-8 [&_h1]:mx-0 [&_h1]:mt-[19px] [&_h1]:mb-4 [&_h1]:text-[26px] [&_h1]:leading-[1.2] [&_h1]:font-semibold [&_h1]:tracking-[-0.8px] [&_p]:m-0 [&_p]:leading-[1.65] [&_p]:text-kumo-subtle [&_p]:text-[15px] min-[761px]:[&_h1]:mx-0 min-[761px]:[&_h1]:mt-0 min-[761px]:[&_h1]:text-[30px] min-[761px]:[&_h1]:leading-[1.2] min-[761px]:[&_h1]:font-semibold min-[761px]:[&_h1]:tracking-[-0.8px]">
            <span className="border border-app-accent-border w-13 h-13 mb-4 inline-grid place-items-center bg-app-accent-surface rounded-[14px] text-kumo-brand">
              <Sparkle size={26} />
            </span>
            <h1 className="text-[30px] leading-[1.2] font-semibold tracking-[-0.8px]" id="new-site-heading">
              Tell us about your business.
            </h1>
            <p>Start with one or two sentences. Our AI assistant will ask for any other details it needs.</p>
          </div>
          <ChatComposer
            value={message}
            onChange={setMessage}
            onSubmit={() => create.mutate()}
            canSend={canStart}
            attachments={attachments}
            label="Tell us about your business."
            autoFocus
          />
          <ErrorNotice error={create.error ?? accounts.error} />
        </div>
      </div>
    </main>
  );
}
